import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import NoticeCard from '../../components/common/NoticeCard';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import LoginModal from '../../components/modals/LoginModal';
import { getPostList, PostListItem, ApiResponse, PostListResponse } from '../../services/postApi';
import { useInfiniteScroll } from '../../hooks/useInfiniteScroll';

const ArchiveHome: React.FC = () => {
  const navigate = useNavigate();
  
  // Redux 상태에서 토큰 가져오기
  const { token } = useSelector((state: RootState) => state.auth);
  
  // 로그인 모달 상태
  const [showLoginModal, setShowLoginModal] = useState(false);
  
  // 로그인 후 이동할 게시글 ID 저장
  const [pendingPostId, setPendingPostId] = useState<number | null>(null);
  
  // 상태 관리
  const [noticePosts, setNoticePosts] = useState<PostListItem[]>([]);
  const [regularPosts, setRegularPosts] = useState<PostListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [hasMore, setHasMore] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);

  // 날짜 포맷팅 함수
  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      const day = date.getDate();
      const hours = date.getHours();
      const minutes = date.getMinutes();
      
      return `${year}년 ${month}월 ${day}일 ${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    } catch (error) {
      /// console.error('날짜 파싱 오류:', error);
      return dateString;
    }
  };

  // API 호출 함수
  const fetchPosts = useCallback(async (page: number = 0, reset: boolean = false) => {
    setLoading(true);
    setError('');

    try {
      const response: ApiResponse<PostListResponse> = await getPostList('archive', page, 10);
      /// console.log(`아카이브 게시글 조회 API 응답`, {page, response});
      
      if (response.code === 200 && response.data) {
        const { content, last } = response.data;
        // /// console.log(`조회된 게시글 수: ${content.length}, 마지막 페이지: ${last}`);
        
        // pinned 여부에 따라 공지사항과 일반 게시글 분리
        const notices = content.filter(post => post.pinned);
        const regulars = content.filter(post => !post.pinned);
        
        if (reset) {
          // 첫 페이지 또는 새로고침
          setNoticePosts(notices);
          setRegularPosts(regulars);
        } else {
          // 다음 페이지 - 공지사항은 보통 첫 페이지에만 있으므로 일반 게시글만 추가
          setRegularPosts(prev => [...prev, ...regulars]);
          if (page === 0) {
            setNoticePosts(notices);
          }
        }
        
        setHasMore(!last);
        setCurrentPage(page);
      } else {
        throw new Error(response.message || '게시글 조회에 실패했습니다.');
      }
    } catch (error: any) {
      /// console.error('게시글 조회 실패:', error);
      setError(error.message || '게시글을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  }, []);

  // 다음 페이지 로드 함수
  const handleLoadMore = useCallback(() => {
    if (hasMore && !loading) {
      fetchPosts(currentPage + 1, false);
    }
  }, [hasMore, loading, currentPage]);

  // 무한스크롤 설정
  useInfiniteScroll({
    hasMore,
    isLoading: loading,
    onLoadMore: handleLoadMore,
  });

  // 컴포넌트 마운트시 데이터 로드
  useEffect(() => {
    fetchPosts(0, true);
  }, []);

  // 게시글 클릭 핸들러
  const handlePostClick = (postId: number) => {
    // Redux에서 토큰 가져오기
    const reduxToken = token;
    // localStorage에서 토큰 가져오기
    const localToken = localStorage.getItem('access_token');
    // 최종 토큰 결정 (Redux 우선, 없으면 localStorage)
    const finalToken = reduxToken || localToken;
    
    // 토큰이 없으면 로그인 모달 띄우기
    if (!finalToken) {
      setPendingPostId(postId); // 로그인 후 이동할 게시글 ID 저장
      setShowLoginModal(true);
      return;
    }
    
    // 토큰이 있으면 게시글 페이지로 이동
    navigate(`/archive/${postId}`);
  };

  // 로그인 성공 후 핸들러
  const handleLoginSuccess = () => {
    setShowLoginModal(false);
    
    // 로그인 후 대기 중인 게시글로 자동 이동
    if (pendingPostId) {
      navigate(`/archive/${pendingPostId}`);
      setPendingPostId(null); // 상태 초기화
    }
  };

  // 로그인 모달 닫기 핸들러
  const handleLoginModalClose = () => {
    setShowLoginModal(false);
    setPendingPostId(null); // 모달을 닫을 때도 상태 초기화
  };

  // 재시도 핸들러
  const handleRetry = () => {
    setCurrentPage(0);
    fetchPosts(0, true);
  };

  // 로딩 상태 처리
  if (initialLoading) {
    return (
      <LayoutContainer>
        <Header />
        <MainContent>
          <ContentArea>
            <Container>
              <MainContainer>
                {/* 페이지 헤더 */}
                <PageHeader>
                  <PageTitle>아카이브</PageTitle>
                </PageHeader>

                {/* 브레드크럼 */}
                <NavigationBar>
                  <BreadcrumbContainer>
                    <BreadcrumbItem>홈</BreadcrumbItem>
                    <ChevronIcon>
                      <svg width="8" height="16" viewBox="0 0 8 16" fill="none">
                        <path 
                          d="M1.73 3.07L6.26 7.6L1.73 12.13" 
                          stroke="rgba(55, 56, 60, 0.61)" 
                          strokeWidth="1.5" 
                          strokeLinecap="round" 
                          strokeLinejoin="round"
                        />
                      </svg>
                    </ChevronIcon>
                    <BreadcrumbItem>아카이브</BreadcrumbItem>
                  </BreadcrumbContainer>
                </NavigationBar>

                {/* 로딩 상태 */}
                <LoadingContainer>
                  <LoadingSpinner />
                </LoadingContainer>
              </MainContainer>
            </Container>
          </ContentArea>
        </MainContent>
        <Footer />
      </LayoutContainer>
    );
  }

  return (
    <LayoutContainer>
      <Header />
      <MainContent>
        <ContentArea>
          <Container>
            <MainContainer>
              {/* 페이지 헤더 */}
              <PageHeader>
                <PageTitle>아카이브</PageTitle>
              </PageHeader>

              {/* 브레드크럼 */}
              <NavigationBar>
                <BreadcrumbContainer>
                  <BreadcrumbItem>홈</BreadcrumbItem>
                  <ChevronIcon>
                    <svg width="8" height="16" viewBox="0 0 8 16" fill="none">
                      <path 
                        d="M1.73 3.07L6.26 7.6L1.73 12.13" 
                        stroke="rgba(55, 56, 60, 0.61)" 
                        strokeWidth="1.5" 
                        strokeLinecap="round" 
                        strokeLinejoin="round"
                      />
                    </svg>
                  </ChevronIcon>
                  <BreadcrumbItem>아카이브</BreadcrumbItem>
                </BreadcrumbContainer>
              </NavigationBar>

              {/* 상단 고정 공지사항 */}
              {noticePosts.length > 0 && (
                <NoticeSection>
                  {noticePosts.map((post) => (
                    <NoticeCard
                      key={`notice-${post.post_id}`}
                      title={post.title}
                      onClick={() => handlePostClick(post.post_id)}
                    />
                  ))}
                </NoticeSection>
              )}

              {/* 에러 상태 */}
              {error && (
                <ErrorContainer>
                  <ErrorMessage>{error}</ErrorMessage>
                  <RetryButton onClick={handleRetry}>다시 시도</RetryButton>
                </ErrorContainer>
              )}

              {/* 게시글 목록 */}
              <PostsList>
                {/* 일반 게시글 */}
                {regularPosts.map((post, index) => (
                  <React.Fragment key={`regular-${post.post_id}`}>
                    <PostCard onClick={() => handlePostClick(post.post_id)}>
                      <PostContent>
                        <PostHeader>
                          <PostTitle>{post.title}</PostTitle>
                        </PostHeader>
                        
                        <PostFooter>
                          <PostAuthorInfo>
                            <AuthorAvatar>
                              {post.writer_profile_image_url ? (
                                <img src={post.writer_profile_image_url} alt="작성자" />
                              ) : (
                                <img src="/images/default-avatar.png" alt="작성자" />
                              )}
                            </AuthorAvatar>
                            <AuthorName>{post.writer_name}</AuthorName>
                            <Divider />
                            <PostDate>{formatDate(post.created_at)}</PostDate>
                          </PostAuthorInfo>
                        </PostFooter>
                      </PostContent>
                    </PostCard>
                    {index < regularPosts.length - 1 && <PostDivider />}
                  </React.Fragment>
                ))}

                {/* 빈 상태 */}
                {!loading && noticePosts.length === 0 && regularPosts.length === 0 && (
                  <EmptyState>
                    <EmptyMessage>게시글이 아직 존재하지 않습니다</EmptyMessage>
                  </EmptyState>
                )}

                {/* 무한스크롤 로딩 표시 */}
                {loading && regularPosts.length > 0 && (
                  <LoadingContainer>
                    <LoadingSpinner />
                  </LoadingContainer>
                )}
              </PostsList>
            </MainContainer>
          </Container>
        </ContentArea>
      </MainContent>
      <Footer />
      
      {/* 로그인 모달 */}
      <LoginModal
        isOpen={showLoginModal}
        onClose={handleLoginModalClose}
        onLoginSuccess={handleLoginSuccess}
      />
    </LayoutContainer>
  );
};

// 스타일 컴포넌트들
const LayoutContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  min-width: 320px;
  overflow-x: hidden;
`;

const MainContent = styled.div`
  display: flex;
  flex: 1;
  width: 100%;
  position: relative;
  min-height: calc(100vh - 80px);
  align-items: flex-start;
  justify-content: center;
`;

const ContentArea = styled.main`
  width: 100%;
  min-height: calc(100vh - 80px);
  background: white;
  display: flex;
  justify-content: center;
  align-items: flex-start;
`;

const Container = styled.div`
  width: 100%;
  min-height: 880px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
`;

const MainContainer = styled.div`
  width: 1200px;
  padding-top: 144px; /* 헤더 높이(80px) + 여유 공간(64px) */
  padding-bottom: 128px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 32px;

  @media (max-width: 1240px) {
    width: calc(100% - 40px);
    margin: 0 20px;
  }
`;

const PageHeader = styled.div`
  align-self: stretch;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 16px;
`;

const PageTitle = styled.h1`
  color: var(--Label-Normal, #171719);
  font-size: 40px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 700;
  line-height: 52px;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 32px;
    line-height: 40px;
  }
`;

const NavigationBar = styled.div`
  align-self: stretch;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const BreadcrumbContainer = styled.div`
  flex: 1;
  height: 32px;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
`;

const BreadcrumbItem = styled.span`
  color: var(--Label-Alternative, rgba(55, 56, 60, 0.61));
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: 0.09px;
`;

const ChevronIcon = styled.div`
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const NoticeSection = styled.div`
  align-self: stretch;
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: -32px;
`;

const ErrorContainer = styled.div`
  align-self: stretch;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 16px;
  padding: 40px 20px;
  background: #fef2f2;
  border-radius: 8px;
  border: 1px solid #fecaca;
`;

const ErrorMessage = styled.p`
  color: #dc2626;
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  text-align: center;
  margin: 0;
`;

const RetryButton = styled.button`
  padding: 8px 16px;
  background: #dc2626;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #b91c1c;
  }
`;

const PostsList = styled.div`
  align-self: stretch;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
`;

const PostCard = styled.div`
  align-self: stretch;
  padding: 32px 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 24px;
  cursor: pointer;

  @media (max-width: 768px) {
    padding: 24px 0;
    gap: 16px;
  }
`;

const PostContent = styled.div`
  align-self: stretch;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 24px;

  @media (max-width: 768px) {
    gap: 16px;
  }
`;

const PostHeader = styled.div`
  align-self: stretch;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 8px;
`;

const PostTitle = styled.h3`
  flex: 1;
  color: var(--Label-Normal, #171719);
  font-size: 18px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 26.01px;
  margin: 0;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;

  @media (max-width: 768px) {
    font-size: 16px;
    line-height: 24px;
  }
`;

const PostFooter = styled.div`
  align-self: stretch;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
`;

const PostAuthorInfo = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
`;

const AuthorAvatar = styled.div`
  position: relative;
  border-radius: 10000px;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 24px;
  height: 24px;
  background: var(--Static-White, white);
  overflow: hidden;
  outline: 1px solid var(--Line-Normal-Alternative, rgba(112, 115, 124, 0.08));
  outline-offset: -1px;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const AuthorName = styled.span`
  color: var(--Label-Neutral, rgba(46, 47, 51, 0.88));
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 20.01px;
  letter-spacing: 0.20px;
`;

const PostDate = styled.span`
  color: var(--Label-Alternative, rgba(55, 56, 60, 0.61));
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 20.01px;
  letter-spacing: 0.20px;
`;

const Divider = styled.div`
  width: 1px;
  height: 12px;
  background: var(--Line-Normal-Normal, rgba(112, 115, 124, 0.22));
`;

const PostStats = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const PostStat = styled.span`
  color: var(--Label-Alternative, rgba(55, 56, 60, 0.61));
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 20.01px;
  letter-spacing: 0.20px;
`;

const PostDivider = styled.div`
  align-self: stretch;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 10px;
  
  &::after {
    content: '';
    align-self: stretch;
    height: 1px;
    background: var(--Line-Normal-Normal, rgba(112, 115, 124, 0.22));
  }
`;

const LoadingContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px 20px;
`;

const EmptyState = styled.div`
  align-self: stretch;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 80px 20px;
`;

const EmptyMessage = styled.p`
  color: var(--Label-Alternative, rgba(55, 56, 60, 0.61));
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  text-align: center;
  margin: 0;
`;

export default ArchiveHome;
